import React from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import { columns, defaultTableOptions } from "../../../../helper-functions/table/transcripts";
import { approveTranscript, approveTranscripts, queryTranscripts, retryCleanupTranscript, retryCleanupTranscripts, retryTranscript, retryTranscripts } from "../../../../helper-functions/api/transcriptQueries";

import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing transcripts.
 * If no data found, show error message instead.
 */
export default function TranscriptsTable(props) {
    const transcripts = props.backend.transcripts;

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete transcript(s)",
            message: `Please delete the resource instead - the transcript will be deleted automatically.

            Otherwise, if you wish to recreate the transcript, please click on the retry icon instead.`,
            actionTitle: "OK",
            cancelTitle: ""
        });
    };

    const showApproveDialog = () => {
        const handle = transcripts.selected.length > 1 ? handleMultiApprove : handleApprove;

        props.showDialog({
            title: "Approve transcript(s)",
            message: `Are you sure you want to approve the ${transcripts.selected.length} selected transcript(s)?

            Make sure you have reviewed the transcript(s) beforehand by clicking on the 'Edit' icon.

            After approval, each transcript will shortly begin further processing by creating transcript chunks and vector embeddings.`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const showRetryDialog = () => {
        const handle = transcripts.selected.length > 1 ? handleMultiRetry : handleRetry;

        props.showDialog({
            title: "Retry transcript(s)",
            message: `Are you sure you want to retry the processing of the ${transcripts.selected.length} selected transcript(s)?
            This rebuilds the formatted text and chunks from the current cleaned captions. It does not re-run LLM cleanup.`,
            actionTitle: "Retry",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const showRetryCleanupDialog = () => {
        const handle = transcripts.selected.length > 1 ? handleMultiRetryCleanup : handleRetryCleanup;

        props.showDialog({
            title: "Retry caption cleanup",
            message: `Re-run LLM cleanup on the ${transcripts.selected.length} selected transcript(s) from the original ingest captions?

            This overwrites the cleaned captions and formatted text, then re-chunks and re-embeds. Manual edits to cleaned lines are discarded. Translations and highlight timestamps are left unchanged.`,
            actionTitle: "Retry cleanup",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const handleUpdate = () => {
        const id = transcripts.selected?.[0]?.id;
        props.browser.navigate(`/database/transcripts/${id}/edit`);
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        const ids = transcripts.selected.map((transcript) => transcript.id);
        approveTranscript(props, { id: ids?.[0] });
    };

    const handleMultiApprove = (confirm) => {
        if (!confirm) return;

        const body = {};
        const ids = transcripts.selected.map((transcript) => transcript.id);
        body.ids = ids;

        approveTranscripts(props, { body });
    };

    const handleRetry = (confirm) => {
        if (!confirm) return;

        const ids = transcripts.selected.map((transcript) => transcript.id);
        retryTranscript(props, { id: ids?.[0] });
    };

    const handleMultiRetry = (confirm) => {
        if (!confirm) return;

        const body = {};
        const ids = transcripts.selected.map((transcripts) => transcripts.id);
        body.ids = ids;

        retryTranscripts(props, { body });
    };

    const handleRetryCleanup = (confirm) => {
        if (!confirm) return;

        const ids = transcripts.selected.map((transcript) => transcript.id);
        retryCleanupTranscript(props, { id: ids?.[0] });
    };

    const handleMultiRetryCleanup = (confirm) => {
        if (!confirm) return;

        const body = {};
        const ids = transcripts.selected.map((transcript) => transcript.id);
        body.ids = ids;

        retryCleanupTranscripts(props, { body });
    };

    return (
        <PaperFragment
            className="tour-transcripts-3 tour-transcripts-4 tour-transcripts-5 tour-transcripts-6"
            title={
                <StackedHeader
                    {...props}
                    title="Transcripts"
                    subtitle="View and manage transcripts."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected transcripts",
                            disabled: transcripts.selected.length === 0 || transcripts.selected.some((t) => ["IN_PROGRESS"].includes(t.status)),
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected transcript",
                            onClick: handleUpdate,
                            disabled: transcripts.selected.length !== 1 || transcripts.selected.some((t) => ["RECREATE", "AWAITING_CLEANUP", "IN_PROGRESS"].includes(t.status))
                        },
                        {
                            icon: <CheckIcon />,
                            tooltip: "Approve selected transcript",
                            onClick: showApproveDialog,
                            disabled: transcripts.selected.length === 0 || transcripts.selected.some((t) => t.status !== "AWAITING_APPROVAL")
                        },
                        {
                            icon: <ReplayIcon />,
                            tooltip: "Retry selected transcript (rebuild chunks, no LLM)",
                            onClick: showRetryDialog,
                            disabled: transcripts.selected.length === 0 || transcripts.selected.some((t) => ["IN_QUEUE", "IN_PROGRESS", "AWAITING_CLEANUP"].includes(t.status))
                        },
                        {
                            icon: <AutoFixHighIcon />,
                            tooltip: "Retry LLM caption cleanup from original ingest",
                            onClick: showRetryCleanupDialog,
                            disabled: transcripts.selected.length === 0 || transcripts.selected.some((t) => ["IN_QUEUE", "IN_PROGRESS", "AWAITING_CLEANUP"].includes(t.status))
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={transcripts}
                    columns={columns(props)}
                    query={(options) => queryTranscripts(props, options)}
                    empty={getLanguage().NO_TRANSCRIPTS_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
